import React from "react";
import { PageHeader, CTASection, SectionTitle } from "@/components/site/Bits";
import { Reveal, ClippedImage, ParallaxImage } from "@/lib/motion";

const VALUES = [
  { t: "Discretion", b: "The finest service is the one you feel, not the one you notice." },
  { t: "Craft", b: "Every cocktail, garnish and gesture is considered and rehearsed." },
  { t: "Grace", b: "We move through a room with the composure the occasion deserves." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="About ARIA"
        title={["Hospitality,", "brought to life."]}
        intro="ARIA was founded on a simple conviction: that the most memorable hospitality is the kind that comes to you."
        testid="about-header"
      />

      <section className="aria-container pb-20 md:pb-28">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-20">
          <div className="md:col-span-7">
            <ParallaxImage src="/api/media/gen-hotel/raw" alt="An elegant ARIA reception" className="aspect-[16/11] w-full" strength={45} />
          </div>
          <div className="md:col-span-5 md:pt-12">
            <Reveal>
              <p className="font-display text-2xl md:text-3xl leading-snug text-aria-ink">
                We began with a question: why should a guest ever have to leave the conversation to find a drink?
              </p>
              <p className="mt-8 font-light text-lg leading-relaxed text-aria-cocoa/80">
                From that question grew a service designed to circulate — a team of trained hosts who carry handcrafted sparkling cocktails through a room, so that hospitality follows the guest rather than the reverse. Today ARIA is entrusted with weddings, embassy receptions, museum galas and private estates across Washington DC, New York and Miami.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-aria-ink text-aria-ivory py-28 md:py-40" data-testid="about-values">
        <div className="aria-container">
          <SectionTitle eyebrow="What Guides Us" title="Three disciplines." dark />
          <div className="mt-16 grid grid-cols-1 gap-12 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.12}>
                <div className="border-t border-aria-ivory/15 pt-8">
                  <h3 className="font-display text-3xl md:text-4xl text-aria-ivory">{v.t}</h3>
                  <p className="mt-5 font-light leading-relaxed text-aria-ivory/60">{v.b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="aria-container py-28 md:py-40">
        <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-20">
          <ClippedImage src="/api/media/real-02/raw" alt="ARIA hosts" aspect="aspect-[4/5]" />
          <div>
            <SectionTitle eyebrow="The Hosts" title={["Chosen for", "their grace."]} />
            <Reveal delay={0.15}>
              <p className="mt-8 font-light text-lg leading-relaxed text-aria-cocoa/80">
                Our hosts are selected and trained not only for their skill, but for their presence — a natural warmth and composure that puts a room at ease. They are, in every sense, the heart of the ARIA experience.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection eyebrow="Enquire" title={["We would love to", "host your evening."]} />
    </div>
  );
}
